// Particle Effect System for Score Updates and Celebrations

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  life: number;
  maxLife: number;
  rotation: number;
  rotationSpeed: number;
  emoji?: string;
}

export class ParticleSystem {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private particles: Particle[] = [];
  private animationFrame: number | null = null;
  private isRunning: boolean = false;

  constructor() {
    this.createCanvas();
  }

  private createCanvas() {
    if (typeof window === 'undefined') return;

    this.canvas = document.createElement('canvas');
    this.canvas.style.position = 'fixed';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '9998'; // Below notifications, above content
    this.canvas.style.background = 'transparent'; // Transparent background

    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;

    this.ctx = this.canvas.getContext('2d', { alpha: true }); // Enable alpha channel
    document.body.appendChild(this.canvas);

    // Handle resize
    window.addEventListener('resize', () => {
      if (this.canvas) {
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;
      }
    });
  }

  private animate = () => {
    if (!this.ctx || !this.canvas) return;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Update and draw particles
    this.particles = this.particles.filter(particle => {
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.vy += 0.2; // Gravity
      particle.life--;
      particle.rotation += particle.rotationSpeed;

      const opacity = particle.life / particle.maxLife;

      if (this.ctx) {
        this.ctx.save();
        this.ctx.translate(particle.x, particle.y);
        this.ctx.rotate(particle.rotation);
        this.ctx.globalAlpha = opacity;

        if (particle.emoji) {
          // Draw emoji
          this.ctx.font = `${particle.size}px Arial`;
          this.ctx.textAlign = 'center';
          this.ctx.textBaseline = 'middle';
          this.ctx.fillText(particle.emoji, 0, 0);
        } else {
          // Draw colored particle
          this.ctx.fillStyle = particle.color;
          this.ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
        }

        this.ctx.restore();
      }

      return particle.life > 0;
    });

    if (this.particles.length > 0) {
      this.animationFrame = requestAnimationFrame(this.animate);
    } else {
      this.isRunning = false;
    }
  };

  private startAnimation() {
    if (!this.isRunning) {
      this.isRunning = true;
      this.animate();
    }
  }

  // Score update celebration - burst of colorful particles
  celebrateScore(x: number, y: number, score: number) {
    const colors = ['#0ea5e9', '#ec4899', '#84cc16', '#8b5cf6', '#f59e0b'];
    const particleCount = Math.min(score / 10, 50); // More particles for higher scores

    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount;
      const speed = 3 + Math.random() * 4;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2, // Initial upward velocity
        size: 8 + Math.random() * 8,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 60 + Math.random() * 40,
        maxLife: 60 + Math.random() * 40,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2
      });
    }

    this.startAnimation();
  }

  // Confetti explosion for big wins
  confettiExplosion(x: number, y: number) {
    const colors = ['#0ea5e9', '#ec4899', '#84cc16', '#8b5cf6', '#f59e0b', '#fbbf24'];

    for (let i = 0; i < 100; i++) {
      const angle = (Math.PI * 2 * i) / 100;
      const speed = 5 + Math.random() * 10;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 5,
        size: 10 + Math.random() * 10,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 100 + Math.random() * 50,
        maxLife: 100 + Math.random() * 50,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.3
      });
    }

    this.startAnimation();
  }

  // Emoji burst for correct answers
  emojiBurst(x: number, y: number, emoji: string = '✨') {
    for (let i = 0; i < 15; i++) {
      const angle = (Math.PI * 2 * i) / 15;
      const speed = 3 + Math.random() * 5;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3,
        size: 20 + Math.random() * 20,
        color: '', // Not used for emoji
        life: 50 + Math.random() * 30,
        maxLife: 50 + Math.random() * 30,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        emoji
      });
    }

    this.startAnimation();
  }

  // Streak celebration - continuous sparkles
  streakCelebration(x: number, y: number, streakCount: number) {
    const emojis = ['🔥', '⭐', '✨', '💫', '🌟'];
    const emoji = streakCount >= 5 ? '🔥' : emojis[Math.floor(Math.random() * emojis.length)];

    for (let i = 0; i < Math.min(streakCount * 3, 30); i++) {
      const angle = (Math.PI * 2 * i) / (streakCount * 3);
      const speed = 2 + Math.random() * 4;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 4,
        size: 25 + Math.random() * 15,
        color: '',
        life: 70 + Math.random() * 40,
        maxLife: 70 + Math.random() * 40,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.25,
        emoji
      });
    }

    this.startAnimation();
  }

  // Star burst for time bonus
  timeBonusStar(x: number, y: number) {
    const colors = ['#fbbf24', '#f59e0b', '#fde047'];

    // Create star points
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8;
      const speed = 6 + Math.random() * 4;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2,
        size: 12 + Math.random() * 8,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 60 + Math.random() * 30,
        maxLife: 60 + Math.random() * 30,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.3
      });
    }

    this.startAnimation();
  }

  // Heart burst for perfect round
  heartBurst(x: number, y: number) {
    for (let i = 0; i < 20; i++) {
      const angle = (Math.PI * 2 * i) / 20;
      const speed = 3 + Math.random() * 5;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3,
        size: 25 + Math.random() * 15,
        color: '',
        life: 70 + Math.random() * 40,
        maxLife: 70 + Math.random() * 40,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        emoji: ['❤️', '💖', '💕', '💗'][Math.floor(Math.random() * 4)]
      });
    }

    this.startAnimation();
  }

  // Clear all particles
  clear() {
    this.particles = [];
    if (this.ctx && this.canvas) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  // Destroy the system
  destroy() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
    this.particles = [];
    this.isRunning = false;
  }
}

// Export singleton instance
export const particles = new ParticleSystem();
